import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, BookOpen, Building2, ChevronRight, IndianRupee, Search, Sparkles, Users } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import config from '../config';

const CATEGORIES = ['All', 'K-12', 'Engineering & Tech', 'Arts & Science', 'Paramedical', 'Education'];
const ALL_CATEGORY_ORDER = {
  'Engineering & Tech': 0,
  'Arts & Science': 1,
  'K-12': 2,
  K12: 2,
};

const getCategoryOrder = (category) => ALL_CATEGORY_ORDER[category] ?? 3;

const formatFullFee = (fee) => {
  const num = Number(fee);
  if (Number.isNaN(num)) return '₹0';
  return `₹${num.toLocaleString('en-IN')}`;
};

const getIconStyle = (name = '') => {
  const colors = [
    { bg: '#EFF6FF', color: '#2563EB' },
    { bg: '#F5F3FF', color: '#7C3AED' },
    { bg: '#ECFDF5', color: '#059669' },
    { bg: '#FFF7ED', color: '#D97706' },
    { bg: '#FFF1F2', color: '#E11D48' },
    { bg: '#F0FDFA', color: '#0D9488' },
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

const getAssetUrl = (path) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  const base = (config.S3_PUBLIC_BASE_URL || '').replace(/\/+$/, '');
  const normalizedPath = String(path).replace(/^\/+/, '');
  if (base) return `${base}/${normalizedPath}`;
  return `${config.API_URL}/${normalizedPath}`;
};

const unwrapApiData = (payload) => (
  payload && typeof payload === 'object' && Object.prototype.hasOwnProperty.call(payload, 'data')
    ? payload.data
    : payload
);

const getInstitutionName = (institution = {}) => (
  institution.displayName || institution.collegeName || institution.institutionName || 'Institution'
);

const groupProgrammesByInstitution = (programmes = []) => {
  const map = new Map();
  programmes.forEach((programme) => {
    const institution = programme.institution || {};
    const institutionId = institution._id || institution.id || programme.institutionId || programme.institutionSlug || institution.slug;
    if (!institutionId) return;
    if (!map.has(institutionId)) {
      map.set(institutionId, {
        ...institution,
        _id: institution._id || institution.id || institutionId,
        id: institution.id || institution._id || institutionId,
        collegeName: institution.collegeName || programme.institutionName || '',
        displayName: institution.displayName || institution.collegeName || programme.institutionName || '',
        slug: institution.slug || programme.institutionSlug || '',
        logoPath: institution.logoPath || '',
        programmes: [],
      });
    }
    map.get(institutionId).programmes.push(programme);
  });
  return Array.from(map.values());
};

export default function CoursesPage() {
  const { user } = useAuth();
  const { collegeSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedProgrammeSlug = searchParams.get('programme') || '';
  const urlSearchQuery = searchParams.get('q') || '';
  const navigate = useNavigate();

  const [institutions, setInstitutions] = useState([]);
  const [institution, setInstitution] = useState(null);
  const [institutionProgrammes, setInstitutionProgrammes] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('All');
  const [hasCompletedApplication, setHasCompletedApplication] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [isSearchDocked, setIsSearchDocked] = useState(false);
  const searchHandoffRef = useRef(null);
  const dockContextRef = useRef({});

  const isCollegeCoursesPage = Boolean(collegeSlug);
  const isSelectedProgrammePage = isCollegeCoursesPage && Boolean(selectedProgrammeSlug);
  const selectedProgrammeName = useMemo(() => {
    if (!selectedProgrammeSlug) return '';
    const matchingCourse = courses.find(course => (
      course.programmeSlug === selectedProgrammeSlug
      || course.programme?.slug === selectedProgrammeSlug
    ));
    if (matchingCourse?.programmeName || matchingCourse?.programme?.name) {
      return matchingCourse.programmeName || matchingCourse.programme.name;
    }
    return selectedProgrammeSlug
      .split('-')
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }, [courses, selectedProgrammeSlug]);
  dockContextRef.current = {
    institutionName: getInstitutionName(institution || {}),
    isSelectedProgrammePage,
  };

  const handleCourseSearch = (value) => {
    const nextParams = new URLSearchParams(searchParams);
    if (value.trim()) {
      nextParams.set('q', value);
    } else {
      nextParams.delete('q');
    }
    setSearchParams(nextParams, { replace: true });
  };

  useEffect(() => {
    let lastDocked = false;
    const updateSearchDockState = () => {
      const rect = searchHandoffRef.current?.getBoundingClientRect();
      if (!rect) return;
      // Separate enter/exit thresholds prevent rapid toggling at the header edge.
      const docked = lastDocked ? rect.top < 96 : rect.top <= 64;
      if (docked === lastDocked) return;
      lastDocked = docked;
      setIsSearchDocked(docked);
      window.dispatchEvent(new CustomEvent('seatify:course-search-dock', {
        detail: { docked, ...dockContextRef.current },
      }));
    };

    updateSearchDockState();
    window.addEventListener('scroll', updateSearchDockState, { passive: true });
    window.addEventListener('resize', updateSearchDockState);

    return () => {
      window.removeEventListener('scroll', updateSearchDockState);
      window.removeEventListener('resize', updateSearchDockState);
      window.dispatchEvent(new CustomEvent('seatify:course-search-dock', { detail: { docked: false } }));
    };
  }, []);

  useEffect(() => {
    if (!isSearchDocked) return;
    window.dispatchEvent(new CustomEvent('seatify:course-search-dock', {
      detail: { docked: true, ...dockContextRef.current },
    }));
  }, [isSearchDocked, institution, isSelectedProgrammePage]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (isCollegeCoursesPage) {
          const url = selectedProgrammeSlug
            ? `${config.API_URL}/api/courses/institution/${collegeSlug}?programme=${selectedProgrammeSlug}`
            : `${config.API_URL}/api/courses/institution/${collegeSlug}`;
          const [res, institutionRes, programmesRes] = await Promise.all([
            axios.get(url),
            axios.get(`${config.API_URL}/api/institutions/${collegeSlug}`).catch(() => ({ data: null })),
            axios.get(`${config.API_URL}/api/programmes`).catch(() => ({ data: [] })),
          ]);
          const payload = unwrapApiData(res.data);
          const institutionPayload = unwrapApiData(institutionRes.data);
          const programmesPayload = unwrapApiData(programmesRes.data);
          const courseRows = Array.isArray(payload) ? payload : (payload?.courses || []);
          setInstitution(institutionPayload || (Array.isArray(payload) ? courseRows[0]?.institution : payload?.institution) || null);
          setInstitutionProgrammes(
            (Array.isArray(programmesPayload) ? programmesPayload : []).filter(programme => (
              programme.institution?.slug === collegeSlug
              || programme.institutionId === institutionPayload?._id
              || programme.institutionId === institutionPayload?.id
            ))
          );
          setCourses(courseRows);
        } else {
          const res = await axios.get(`${config.API_URL}/api/programmes`);
          const payload = unwrapApiData(res.data);
          setInstitutions(groupProgrammesByInstitution(Array.isArray(payload) ? payload : []));
        }
      } catch (err) {
        console.error('Course page fetch error:', err);
        toast.error(isCollegeCoursesPage ? 'Failed to load institution courses' : 'Failed to load programmes');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isCollegeCoursesPage, collegeSlug, selectedProgrammeSlug]);

  useEffect(() => {
    if (!user) return;
    const checkCompletedApplication = async () => {
      try {
        const res = await axios.get('/api/applications/my');
        setHasCompletedApplication(Boolean(res.data?.hasActiveApplication));
      } catch (err) {
        console.error('Error checking completed applications:', err);
      }
    };
    checkCompletedApplication();
  }, [user]);

  const filteredInstitutions = useMemo(() => {
    const q = urlSearchQuery.trim().toLowerCase();
	    const matchingInstitutions = institutions.filter((item) => {
	      const matchesText = !q
	        || item.collegeName?.toLowerCase().includes(q)
	        || item.displayName?.toLowerCase().includes(q)
        || item.programmes?.some(programme => programme.name?.toLowerCase().includes(q));
	      const matchesTab = activeTab === 'All' || item.programmes?.some(programme => programme.category === activeTab);
	      return matchesText && matchesTab;
	    });
	    if (activeTab !== 'All') return matchingInstitutions;
	    return [...matchingInstitutions].sort((a, b) => {
	      const aCategory = a.category || a.programmes?.[0]?.category;
	      const bCategory = b.category || b.programmes?.[0]?.category;
	      return getCategoryOrder(aCategory) - getCategoryOrder(bCategory);
	    });
	  }, [institutions, urlSearchQuery, activeTab]);

  const filteredCourses = useMemo(() => {
    const q = urlSearchQuery.trim().toLowerCase();
    return courses.filter((course) => {
      const matchesText = !q
        || course.name?.toLowerCase().includes(q)
        || course.programmeName?.toLowerCase().includes(q)
        || course.type?.toLowerCase().includes(q);
      const matchesTab = activeTab === 'All' || course.category === activeTab;
      return matchesText && matchesTab;
    });
  }, [courses, urlSearchQuery, activeTab]);

  const handleApply = (course) => {
    if (course.active === false) {
      toast.error('Course registration is closed');
      return;
    }
    const programme = course.programme || {
      _id: course.programmeId || course.programme,
      name: course.programmeName,
      slug: course.programmeSlug,
      category: course.category,
    };

    if (!user) {
      toast('Please login to start your application', { icon: '👋' });
      navigate('/login', { state: { from: `/apply/${course._id}`, course, program: programme } });
      return;
    }
    if (hasCompletedApplication) {
      setShowWarningModal(true);
      return;
    }
    navigate(`/apply/${course._id}`, { state: { course, program: programme } });
  };

  const handleProgrammeChange = (programmeSlug) => {
    const nextParams = new URLSearchParams(searchParams);
    if (programmeSlug) nextParams.set('programme', programmeSlug);
    else nextParams.delete('programme');
    nextParams.delete('q');
    setSearchParams(nextParams);
  };

  return (
    <div className="min-h-screen relative user-gradient-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8 pt-10 relative">
        <div className="text-center mb-10 animate-fade-up">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium mb-4 user-soft-gradient"
            style={{ color: 'var(--primary)' }}>
            <Sparkles size={13} />
            Secure Your Seat Today
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-3 user-gradient-text" style={{ fontFamily: 'Poppins' }}>
            {isCollegeCoursesPage
              ? `${institution?.displayName || institution?.collegeName || 'Institution'}${selectedProgrammeName ? ` · ${selectedProgrammeName}` : ''} Courses`
              : 'Institutions & Programmes'}
          </h1>
          <p style={{ color: 'var(--text-muted)', maxWidth: '620px', margin: '0 auto', lineHeight: 1.7 }}>
            {isCollegeCoursesPage
              ? 'Choose a course from this institution and continue your admission journey.'
              : 'Choose an institution programme first, then view all available courses for that institution.'}
          </p>
        </div>

      </div>

	      <div ref={searchHandoffRef} className="h-0" aria-hidden="true" />
	      <div
	        className={`sticky top-[64px] z-50 mb-8 pb-3 transition-all duration-300 ease-out ${
	          isSelectedProgrammePage ? '' : 'px-4 md:px-8 backdrop-blur-xl'
	        }`}
	        style={{
	          background: isSelectedProgrammePage
	            ? 'transparent'
	            : 'linear-gradient(180deg, rgba(248,250,255,0.98) 0%, rgba(248,250,252,0.92) 100%)',
	        }}>
		        <div className={`mx-auto transition-all duration-300 ease-out ${
		          isSelectedProgrammePage ? 'max-w-7xl px-4 md:px-8' : 'max-w-3xl'
		        }`}>
		          {isSelectedProgrammePage ? (
		            <div className="grid grid-cols-[auto_minmax(0,1fr)] md:grid-cols-3 items-center gap-2 md:gap-3 rounded-2xl border border-indigo-100/80 bg-white/90 p-2 shadow-lg shadow-indigo-100/60 backdrop-blur-xl">
		              <div className="flex justify-start">
		                <button
	                  type="button"
	                  onClick={() => navigate('/courses')}
	                  className="inline-flex items-center gap-2 rounded-xl border border-indigo-100 bg-white/90 px-3.5 py-3 text-sm font-medium text-slate-700 transition-colors hover:bg-indigo-50 hover:text-indigo-700"
	                >
	                  <ArrowLeft size={17} />
	                  <span className="hidden sm:inline">Back</span>
	                </button>
	              </div>

		              <div className="min-w-0 md:flex md:justify-center">
	                <select
	                  aria-label="Select programme"
	                  value={selectedProgrammeSlug}
	                  onChange={(event) => handleProgrammeChange(event.target.value)}
		                  className="w-full min-w-0 md:max-w-xs rounded-xl border border-indigo-100 bg-white/90 px-3 md:px-4 py-3 text-sm font-medium text-slate-700 outline-none transition-all focus:ring-2 focus:ring-indigo-500/20"
	                >
	                  {institutionProgrammes.length === 0 && (
	                    <option value={selectedProgrammeSlug}>{selectedProgrammeName}</option>
	                  )}
	                  {institutionProgrammes.map(programme => (
	                    <option key={programme._id || programme.id || programme.slug} value={programme.slug}>
	                      {programme.name}
	                    </option>
	                  ))}
	                </select>
	              </div>

		              <div className="relative group col-span-2 min-w-0 md:col-span-1">
	                <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
	                  <Search size={18} style={{ color: 'var(--text-muted)' }} />
	                </div>
	                <input
	                  type="text"
	                  placeholder="Search courses..."
	                  value={urlSearchQuery}
	                  onChange={(e) => handleCourseSearch(e.target.value)}
	                  className="w-full pl-12 pr-4 py-3 rounded-xl text-sm outline-none user-glass-surface focus:ring-2 focus:ring-indigo-500/20 transition-all duration-300 ease-out"
	                  style={{ color: 'var(--text)' }}
	                />
	              </div>
	            </div>
	          ) : (
	          <div
	            className={`relative group overflow-hidden transition-all duration-300 ease-out ${
	              isSearchDocked ? 'max-h-0 opacity-0 -translate-y-2 pointer-events-none' : 'max-h-20 opacity-100 translate-y-0'
            }`}
          >
            <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
              <Search size={18} style={{ color: 'var(--text-muted)' }} />
            </div>
            <input
              type="text"
              placeholder={isCollegeCoursesPage ? 'Search courses...' : 'Search institutions or programmes...'}
              value={urlSearchQuery}
              onChange={(e) => handleCourseSearch(e.target.value)}
              className="w-full pl-12 pr-12 py-3.5 rounded-2xl text-sm outline-none user-glass-surface focus:ring-2 focus:ring-indigo-500/20 transition-all duration-300 ease-out"
	              style={{ color: 'var(--text)' }}
	            />
	          </div>
	          )}
          {!isCollegeCoursesPage && (
	            <div className="course-category-scroll flex gap-2 overflow-x-auto p-1 justify-start md:justify-center mt-1">
              {CATEGORIES.map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className="flex-shrink-0 px-4 py-2 rounded-full text-sm  transition-all shadow-sm hover:shadow-md"
                  style={{
                    background: activeTab === tab ? 'var(--primary-gradient)' : 'rgba(255,255,255,0.9)',
                    color: activeTab === tab ? '#fff' : '#64748B',
                    border: activeTab === tab ? 'none' : '1px solid rgba(199,210,254,0.7)',
                  }}>
                  {tab}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 pb-32 md:pb-8">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map(i => <div key={i} className="rounded-2xl h-48 animate-pulse bg-white border" />)}
          </div>
        ) : isCollegeCoursesPage ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredCourses.length === 0 ? (
              <div className="user-glass-surface p-8 rounded-2xl text-center text-slate-500 md:col-span-2 xl:col-span-3">No courses found.</div>
            ) : filteredCourses.map((course) => {
              const style = getIconStyle(course.programmeName || course.name);
              const remainingSeats = Number(course.seatsAvailable ?? course.seats ?? 0);
              const seatsFilled = remainingSeats <= 0;
              return (
                <div key={course._id} className="course-card user-glass-surface rounded-3xl p-6">
                  <div className="flex items-start gap-4 mb-5">
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: style.bg, color: style.color }}>
                      <BookOpen size={22} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-medium uppercase tracking-wider text-indigo-500 mb-1">{course.programmeName}</p>
                      <h3 className="font-semibold text-lg text-slate-900 leading-tight">{course.name}</h3>
                      <p className="text-sm text-slate-500 mt-1">{course.type || course.category}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm mb-5">
                    <div className="rounded-xl bg-slate-50 p-3">
                      <p className="text-xs text-slate-500 mb-1">Fee</p>
                      <p className="font-bold text-slate-900">{formatFullFee(course.fee)}</p>
                    </div>
                    <div className={`rounded-xl p-3 ${seatsFilled ? 'bg-rose-50' : 'bg-slate-50'}`}>
                      <p className="text-xs text-slate-500 mb-1">Seats</p>
                      <p className={`font-bold ${seatsFilled ? 'text-rose-600' : 'text-slate-900'}`}>
                        {seatsFilled ? 'Seats filled' : remainingSeats}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mb-5 min-h-5">{course.minimumQualification || 'Minimum qualification will be verified.'}</p>
                  {course.active === false ? (
                    <button
                      disabled
                      className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-bold text-slate-400 bg-slate-100 border border-slate-200 cursor-not-allowed"
                    >
                      Registration Closed
                    </button>
                  ) : seatsFilled ? (
                    <button
                      disabled
                      className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-bold text-rose-500 bg-rose-50 border border-rose-200 cursor-not-allowed"
                    >
                      Seats Filled
                    </button>
                  ) : (
                    <button
                      onClick={() => handleApply(course)}
                      className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-bold text-white transition-all hover:shadow-lg hover:shadow-indigo-500/30 user-gradient-fill"
                    >
                      Apply Now <ChevronRight size={16} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
		          <div className="grid grid-cols-1 lg:grid-cols-2 grid-flow-dense gap-6">
	            {filteredInstitutions.length === 0 ? (
	              <div className="user-glass-surface p-8 rounded-2xl text-center text-slate-500">No programmes found.</div>
            ) : filteredInstitutions.map((item) => {
              const style = getIconStyle(getInstitutionName(item));
              const logoUrl = getAssetUrl(item.logoPath);
	              const visibleProgrammes = activeTab === 'All'
	                ? [...(item.programmes || [])].sort(
	                    (a, b) => getCategoryOrder(a.category) - getCategoryOrder(b.category)
	                  )
	                : (item.programmes || []).filter(programme => programme.category === activeTab);
              return (
		                <div
		                  key={item._id || item.slug}
		                  className={`course-card user-glass-surface rounded-3xl p-6 h-full flex flex-col ${
		                    visibleProgrammes.length > 2 ? 'lg:col-span-2' : ''
		                  }`}
		                >
	                  <div className="flex items-center gap-4 mb-6">
	                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center overflow-hidden border border-slate-100" style={{ background: style.bg, color: style.color }}>
	                      {logoUrl ? <img src={logoUrl} alt={getInstitutionName(item)} className="w-full h-full object-contain p-1 bg-white" /> : <Building2 size={26} />}
	                    </div>
	                    <div>
	                      <h3 className="font-semibold text-xl text-slate-900">{getInstitutionName(item)}</h3>
	                      <p className="text-sm text-slate-500 flex items-center gap-1 mt-1"><Users size={14} /> {visibleProgrammes.length} programmes</p>
	                    </div>
	                  </div>

		                  <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${
		                    visibleProgrammes.length > 2 ? 'lg:grid-cols-4' : 'lg:grid-cols-2'
		                  }`}>
                    {visibleProgrammes.map((programme) => (
                      <button
                        key={programme._id}
                        onClick={() => navigate(`/${item.slug}/courses?programme=${programme.slug}`)}
	                        className="text-left rounded-2xl border border-indigo-100 bg-white/75 hover:bg-indigo-50 hover:border-indigo-200 p-4 transition-all"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div>
	                            <p className="font-medium text-slate-800">{programme.name}</p>
	                            <p className="text-xs text-slate-500 mt-1">{programme.category}</p>
                          </div>
                          <ChevronRight size={18} className="text-indigo-500" />
                        </div>
                      </button>
                    ))}
                  </div>

	                  <div className="mt-auto pt-5">
	                    <button
	                      onClick={() => navigate(`/${item.slug}/courses`)}
	                      className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold border border-indigo-100 text-indigo-700 bg-white/75 hover:bg-indigo-50"
	                    >
	                      View All Courses <ChevronRight size={16} />
	                    </button>
	                  </div>
	                </div>
              );
            })}
          </div>
        )}
      </div>

      {showWarningModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-sm w-full text-center">
            <h3 className="text-xl font-bold mb-2">Application Limit Reached</h3>
            <p className="text-slate-500 mb-6">You have reached the limit for this academic year.</p>
            <button onClick={() => setShowWarningModal(false)} className="w-full py-3 bg-indigo-600 text-white rounded-xl font-bold">OK</button>
          </div>
        </div>
      )}
    </div>
  );
}
